import React, { useEffect, useState } from 'react';
import { getMyBusinesses } from './MiniERPPriceFlowAPI';
import MiniERPPriceFlowBusinessProfile from './MiniERPPriceFlowBusinessProfile';
import MiniERPPriceFlowProductList from './MiniERPPriceFlowProductList';
import MiniERPPriceFlowPublishPage from './MiniERPPriceFlowPublishPage';

const VIEWS = { LOADING: 'loading', ONBOARD: 'onboard', HOME: 'home', EDIT: 'edit', PRODUCTS: 'products', PUBLISH: 'publish' };

// Drop into your routing, e.g.:
//   import LivePriceHome from './priceflow/MiniERPPriceFlowLivePriceHome';
//   <Route path="/priceflow" element={<LivePriceHome />} />
export default function MiniERPPriceFlowLivePriceHome() {
  const [view, setView] = useState(VIEWS.LOADING);
  const [business, setBusiness] = useState(null);
  const [error, setError] = useState('');

  const load = async () => {
    setView(VIEWS.LOADING);
    try {
      const businesses = await getMyBusinesses();
      if (businesses.length) {
        setBusiness(businesses[0]);
        setView(VIEWS.HOME);
      } else {
        setView(VIEWS.ONBOARD);
      }
    } catch (err) {
      setError(err.message);
      setView(VIEWS.ONBOARD);
    }
  };

  useEffect(() => { load(); }, []);

  if (view === VIEWS.LOADING) {
    return (
      <div className="min-h-screen bg-[#0B0B12] flex items-center justify-center">
        <p className="text-white/40 text-sm">Loading…</p>
      </div>
    );
  }

  if (view === VIEWS.ONBOARD) {
    return (
      <MiniERPPriceFlowBusinessProfile
        onSaved={(biz) => { setBusiness(biz); setView(VIEWS.HOME); }}
        onBack={() => window.history.back()}
      />
    );
  }

  if (view === VIEWS.EDIT) {
    return (
      <MiniERPPriceFlowBusinessProfile
        initial={business}
        onSaved={(biz) => { setBusiness(biz); setView(VIEWS.HOME); }}
        onBack={() => setView(VIEWS.HOME)}
      />
    );
  }

  if (view === VIEWS.PRODUCTS) {
    return <MiniERPPriceFlowProductList businessId={business._id} onBack={() => setView(VIEWS.HOME)} />;
  }

  if (view === VIEWS.PUBLISH) {
    return (
      <MiniERPPriceFlowPublishPage
        business={business}
        onBack={() => setView(VIEWS.HOME)}
        onPublished={(updated) => setBusiness(updated)}
      />
    );
  }

  // HOME
  return (
    <div className="min-h-screen bg-[#0B0B12] text-white px-4 py-10 sm:px-8 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-32 -left-32 w-96 h-96 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-32 w-96 h-96 rounded-full bg-cyan-400/10 blur-3xl" />

      <div className="max-w-3xl mx-auto relative">
        <p className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-2">MiniERP · PriceFlow</p>

        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{business.businessName}</h1>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-xs text-white/40">Version {business.version} · {business.status === 'active' ? 'Live' : 'Draft — not published yet'}</span>
            </div>
          </div>
          <button onClick={() => setView(VIEWS.EDIT)} className="shrink-0 px-3 py-2 rounded-xl text-xs font-semibold bg-white/5 ring-1 ring-white/10 hover:bg-white/10 transition">
            Edit Business
          </button>
        </div>

        {error && <p className="text-rose-400 text-sm mb-4">{error}</p>}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setView(VIEWS.PRODUCTS)}
            className="group relative text-left rounded-3xl p-[1px] bg-gradient-to-br from-white/10 to-white/0 hover:from-white/20 transition"
          >
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-cyan-500/40 to-emerald-500/40 opacity-0 group-hover:opacity-100 blur-xl transition duration-300" />
            <div className="relative rounded-3xl bg-[#14141C] p-6 h-full">
              <span className="text-3xl">🏷️</span>
              <h3 className="mt-3 font-bold text-lg tracking-tight">Products &amp; Prices</h3>
              <p className="text-sm text-white/40 mt-1">Add, edit, reorder, and set offers</p>
            </div>
          </button>

          <button
            onClick={() => setView(VIEWS.PUBLISH)}
            className="group relative text-left rounded-3xl p-[1px] bg-gradient-to-br from-white/10 to-white/0 hover:from-white/20 transition"
          >
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-500/40 to-teal-400/40 opacity-0 group-hover:opacity-100 blur-xl transition duration-300" />
            <div className="relative rounded-3xl bg-[#14141C] p-6 h-full">
              <span className="text-3xl">🚀</span>
              <h3 className="mt-3 font-bold text-lg tracking-tight">Publish &amp; Share</h3>
              <p className="text-sm text-white/40 mt-1">Go live, copy your link, check views</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}
